import { Injectable } from '@angular/core';

import { AssetLineData, InputDetails, LineData } from '../../util/calculation.interface';

@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  selectedAssetGrowthData(inputDetails: InputDetails): AssetLineData {
    let assetGrowthData: AssetLineData = {
      conversionLineData: [],
      nonConversionLineData: [],
    };
    switch (inputDetails.conversionTaxes) {
      case 'cash':
        assetGrowthData = this.assetGrowthCashConversionTaxData(inputDetails);
        break;
      case 'tax-inefficient':
        assetGrowthData = this.assetGrowthInefficientConversionTaxData(inputDetails);
        break;
      case 'tax-efficient':
        assetGrowthData = this.assetGrowthEfficientConversionTaxData(inputDetails);
        break;
      case 'ira':
        assetGrowthData = this.assetGrowthIraConversionTaxData(inputDetails);
        break;
    }
    return assetGrowthData;
  }

  pushData(
    assetLineData: AssetLineData,
    year: number,
    conversionValue: number,
    nonConversionValue: number,
  ) {
    assetLineData.conversionLineData.push({ year: year, value: conversionValue } as LineData);
    assetLineData.nonConversionLineData.push({ year: year, value: nonConversionValue } as LineData);
  }

  assetGrowthCashConversionTaxData(inputDetails: InputDetails): AssetLineData {
    const rothBasisAmt = inputDetails.iraBasis * inputDetails.rothConvAmt;
    const assetLineData: AssetLineData = { conversionLineData: [], nonConversionLineData: [] };

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      // theoretical investment growth of tax liability from the roth conversion (cash returns)
      const taxGrowth =
        (inputDetails.rothConvAmt - rothBasisAmt) *
        inputDetails.currentTaxRate *
        (1 + (1 - inputDetails.currentTaxRate) * inputDetails.cashYield) ** year;

      const conversionValue = inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year;
      const nonConversionValue =
        (inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year - rothBasisAmt) *
          (1 - inputDetails.futureTaxRate) +
        rothBasisAmt +
        taxGrowth;

      this.pushData(assetLineData, year, conversionValue, nonConversionValue);
    }

    return assetLineData;
  }

  assetGrowthInefficientConversionTaxData(inputDetails: InputDetails): AssetLineData {
    const rothBasisAmt = inputDetails.iraBasis * inputDetails.rothConvAmt;
    const assetLineData: AssetLineData = { conversionLineData: [], nonConversionLineData: [] };

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      // theoretical investment growth of tax liability from the roth conversion
      // (inefficient portfolio - year.e., taxed annually at the short-term cap gains rate)
      const taxGrowth =
        (inputDetails.rothConvAmt - rothBasisAmt) *
        inputDetails.currentTaxRate *
        (1 + (1 - inputDetails.currentTaxRate) * inputDetails.annualReturn) ** year;

      const conversionValue = inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year;
      const nonConversionValue =
        (inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year - rothBasisAmt) *
          (1 - inputDetails.futureTaxRate) +
        rothBasisAmt +
        taxGrowth;

      this.pushData(assetLineData, year, conversionValue, nonConversionValue);
    }

    return assetLineData;
  }

  assetGrowthEfficientConversionTaxData(inputDetails: InputDetails): AssetLineData {
    const rothBasisAmt = inputDetails.iraBasis * inputDetails.rothConvAmt;
    const assetLineData: AssetLineData = { conversionLineData: [], nonConversionLineData: [] };

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      const taxableDividendYield =
        (inputDetails.dividendTaxRate / inputDetails.currentTaxRate) * inputDetails.dividendYield;
      // St in Boris's manuscript
      const taxGrowth =
        (inputDetails.rothConvAmt - rothBasisAmt) *
        inputDetails.currentTaxRate *
        (1 + inputDetails.annualReturn - inputDetails.currentTaxRate * taxableDividendYield) **
          year;
      const divGrowthRate =
        1 +
        ((1 - inputDetails.currentTaxRate) *
          taxableDividendYield *
          (1 -
            (1 + inputDetails.annualReturn - inputDetails.currentTaxRate * taxableDividendYield) **
              year)) /
          (inputDetails.currentTaxRate * taxableDividendYield - inputDetails.annualReturn);
      // Bt in Boris's manuscript
      const divGrowthNominal =
        inputDetails.currentTaxRate * (inputDetails.rothConvAmt - rothBasisAmt) * divGrowthRate;

      const conversionValue = inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year;
      const nonConversionValue =
        inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year -
        inputDetails.futureTaxRate *
          (inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year - rothBasisAmt) +
        taxGrowth -
        inputDetails.futureTaxRate *
          (inputDetails.gainTaxRate / inputDetails.currentTaxRate) *
          (taxGrowth - divGrowthNominal);

      this.pushData(assetLineData, year, conversionValue, nonConversionValue);
    }

    return assetLineData;
  }

  assetGrowthIraConversionTaxData(inputDetails: InputDetails): AssetLineData {
    const rothBasisAmt = inputDetails.iraBasis * inputDetails.rothConvAmt;
    const assetLineData: AssetLineData = { conversionLineData: [], nonConversionLineData: [] };

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      const conversionValue =
        (inputDetails.rothConvAmt -
          (inputDetails.rothConvAmt - rothBasisAmt) * inputDetails.currentTaxRate) *
        (1 + inputDetails.annualReturn) ** year;
      const nonConversionValue =
        (inputDetails.rothConvAmt * (1 + inputDetails.annualReturn) ** year - rothBasisAmt) *
          (1 - inputDetails.futureTaxRate) +
        rothBasisAmt;

      this.pushData(assetLineData, year, conversionValue, nonConversionValue);
    }

    return assetLineData;
  }

  selectedBreakEvenTaxRateData(inputDetails: InputDetails): LineData[] {
    let breakEvenTaxData: LineData[] = [];
    switch (inputDetails.conversionTaxes) {
      case 'cash':
        breakEvenTaxData = this.breakEvenCashConversionTaxData(inputDetails);
        break;
      case 'tax-inefficient':
        breakEvenTaxData = this.breakEvenInefficientConversionTaxData(inputDetails);
        break;
      case 'tax-efficient':
        breakEvenTaxData = this.breakEvenEfficientConversionTaxData(inputDetails);
        break;
      case 'ira':
        breakEvenTaxData = this.breakEvenIraConversionTaxData(inputDetails);
        break;
    }
    return breakEvenTaxData;
  }

  private calculateBreakEvenRate(numerator: number, denominator: number): number {
    return denominator === 0 ? 0 : Math.round((numerator / denominator) * 10000) / 10000;
  }

  breakEvenCashConversionTaxData(inputDetails: InputDetails): LineData[] {
    const breakEvenTaxRateData: LineData[] = [];
    const rothConvAmt: number = inputDetails.rothConvAmt || 0;

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      const iraBasisAmt: number = rothConvAmt * inputDetails.iraBasis;

      const denominator = rothConvAmt * (1 + inputDetails.annualReturn) ** year - iraBasisAmt;
      const numerator =
        (rothConvAmt - iraBasisAmt) *
        inputDetails.currentTaxRate *
        (1 + (1 - inputDetails.currentTaxRate) * inputDetails.cashYield) ** year;
      const betr = this.calculateBreakEvenRate(numerator, denominator);
      breakEvenTaxRateData.push({ year: year, value: betr });
    }
    return breakEvenTaxRateData;
  }

  breakEvenInefficientConversionTaxData(inputDetails: InputDetails): LineData[] {
    const breakEvenTaxRateData: LineData[] = [];
    const rothConvAmt: number = inputDetails.rothConvAmt || 0;

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      const iraBasisAmt: number = rothConvAmt * inputDetails.iraBasis;

      const denominator = rothConvAmt * (1 + inputDetails.annualReturn) ** year - iraBasisAmt;
      const numerator =
        (rothConvAmt - iraBasisAmt) *
        inputDetails.currentTaxRate *
        (1 + (1 - inputDetails.currentTaxRate) * inputDetails.annualReturn) ** year;
      const betr = this.calculateBreakEvenRate(numerator, denominator);

      breakEvenTaxRateData.push({ year: year, value: betr });
    }
    return breakEvenTaxRateData;
  }

  breakEvenEfficientConversionTaxData(inputDetails: InputDetails): LineData[] {
    const breakEvenTaxRateData: LineData[] = [];
    const rothConvAmt: number = inputDetails.rothConvAmt || 0;

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      const iraBasisAmt: number = rothConvAmt * inputDetails.iraBasis;

      const diff =
        1 +
        ((1 - inputDetails.currentTaxRate) *
          (inputDetails.dividendTaxRate / inputDetails.currentTaxRate) *
          inputDetails.dividendYield *
          (1 -
            (1 +
              inputDetails.annualReturn -
              inputDetails.currentTaxRate *
                (inputDetails.dividendTaxRate / inputDetails.currentTaxRate) *
                inputDetails.dividendYield) **
              year)) /
          (inputDetails.currentTaxRate *
            (inputDetails.dividendTaxRate / inputDetails.currentTaxRate) *
            inputDetails.dividendYield -
            inputDetails.annualReturn);
      const denominator =
        rothConvAmt * (1 + inputDetails.annualReturn) ** year -
        iraBasisAmt +
        (inputDetails.gainTaxRate / inputDetails.currentTaxRate) *
          inputDetails.currentTaxRate *
          (rothConvAmt - iraBasisAmt) *
          ((1 +
            inputDetails.annualReturn -
            (inputDetails.dividendTaxRate / inputDetails.currentTaxRate) *
              inputDetails.currentTaxRate *
              inputDetails.dividendYield) **
            year -
            diff);
      const numerator =
        (rothConvAmt - iraBasisAmt) *
        inputDetails.currentTaxRate *
        (1 +
          inputDetails.annualReturn -
          (inputDetails.dividendTaxRate / inputDetails.currentTaxRate) *
            inputDetails.currentTaxRate *
            inputDetails.dividendYield) **
          year;
      const betr = this.calculateBreakEvenRate(numerator, denominator);

      breakEvenTaxRateData.push({ year: year, value: betr });
    }
    return breakEvenTaxRateData;
  }

  breakEvenIraConversionTaxData(inputDetails: InputDetails): LineData[] {
    const breakEvenTaxRateData: LineData[] = [];
    const rothConvAmt: number = inputDetails.rothConvAmt || 0;

    for (let year = graphSpecs.start; year < graphSpecs.end; year += graphSpecs.interval) {
      const iraBasisAmt: number = rothConvAmt * inputDetails.iraBasis;

      const denominator = rothConvAmt * (1 + inputDetails.annualReturn) ** year - iraBasisAmt;
      const numerator =
        (rothConvAmt - iraBasisAmt) *
        inputDetails.currentTaxRate *
        (1 + inputDetails.annualReturn) ** year;
      const betr = this.calculateBreakEvenRate(numerator, denominator);

      breakEvenTaxRateData.push({ year: year, value: betr });
    }
    return breakEvenTaxRateData;
  }
}

export const graphSpecs = {
  start: 0,
  end: 51,
  interval: 1,
};
